import { AwsRum, AwsRumConfig } from "aws-rum-web";
import { createContext, useMemo } from "react";
import { useAuth } from "react-oidc-context";

export const RumContext = createContext<AwsRum | undefined>(undefined);

interface RumProviderProps {
  children: React.ReactNode;
}

// Singleton instance of RUM
let rumInstance: AwsRum | undefined;

function initializeRum(isAuthenticated: boolean, userEmail?: string) {
  // Only initialize if authenticated and not already set
  if (rumInstance || !isAuthenticated) {
    return rumInstance;
  }

  try {
    const config: AwsRumConfig = {
      disableAutoPageView: true,
      enableRumClient: true,
      sessionSampleRate: 1,
      identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID!,
      endpoint: `https://dataplane.rum.${process.env.REACT_APP_COGNITO_REGION}.amazonaws.com`,
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: true,
      // guestRoleArn: process.env.REACT_APP_COGNITO_IDENTITY_POOL_UNAUTHENTICATED_ROLE!,
    };

    const APPLICATION_ID: string = process.env.REACT_APP_RUM_APP_ID!;
    const APPLICATION_VERSION: string = '1.0.0';
    const APPLICATION_REGION: string = process.env.REACT_APP_COGNITO_REGION!;

    rumInstance = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      config
    );

    if (userEmail) {
      rumInstance.addSessionAttributes({ userEmail });
    }
  } catch (error) {
    console.error('RUM initialization error:', error);
    rumInstance = undefined;
  }
  return rumInstance;
}

export function RumProvider({ children }: RumProviderProps) {
  const auth = useAuth();
  const userEmail = auth.user?.profile.email;
  const rum = useMemo(
    () => initializeRum(auth.isAuthenticated, userEmail),
    [auth.isAuthenticated, userEmail]
  );

  return (
    <RumContext.Provider value={rum}>
      {children}
    </RumContext.Provider>
  );
}
